package org.example.project.screens_shared

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.browser.window
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.json.Json
import org.example.project.enginekalender.KalenderWeekDetail
import org.example.project.enginekalender.kalenderAddDays
import org.example.project.enginekalender.Monatsübersicht
import org.example.project.enginekalender.parseKalenderIsoDate
import org.example.project.enginekalender.kalenderTodayIsoDate
import org.example.project.engineservice.schulung.SchulungApi
import org.example.project.manager.LumaticaApiManager
import org.example.project.manager.NodeScope
import org.example.project.service.LoginService
import org.example.project.service.GlobalManager
import org.example.project.service.ZustandManager
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.events.Event

private val terminScreenJson = Json {
    ignoreUnknownKeys = true
    encodeDefaults = false
}

private fun terminApiPart(value: String): String =
    js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String

@Composable
fun SHAREDTerminverwaltung(
    art: String,
    node: String,
    sourceArt: String = art,
    sourceUserId: String? = null,
    sourceFirmenId: String = "",
    serviceBookingMode: Boolean = false,
    bookingTemplate: All? = null,
    onBookingSaved: ((All) -> Unit)? = null
) {
    val loginUser = LoginService.getUser()
    val schulungLiveVersion = GlobalManager.jsonDomainVersion("schulung")
    val effectiveArt = sourceArt
    val allowsKiSchulung = effectiveArt.uppercase() == NodeScope.SYS || effectiveArt.uppercase() == NodeScope.CUS
    val effectiveUserId = sourceUserId ?: loginUser?.id.orEmpty()
    val loginFirmenId = loginUser?.firmenId.orEmpty().trim()
    val effectiveSourceFirmenId = sourceFirmenId.trim().ifBlank {
        if (effectiveArt.uppercase() == NodeScope.SYS) "" else loginFirmenId
    }
    val isOwnCalendar = remember(loginFirmenId, loginUser?.id, effectiveUserId, effectiveSourceFirmenId) {
        val loginUserId = loginUser?.id.orEmpty().trim()
        val targetFirmenId = effectiveSourceFirmenId.ifBlank { loginFirmenId }
        loginUserId.isNotBlank() &&
            effectiveUserId.trim() == loginUserId &&
            targetFirmenId == loginFirmenId
    }
    val calendarContextKey = remember(effectiveArt, effectiveUserId, effectiveSourceFirmenId, serviceBookingMode) {
        listOf(
            effectiveArt,
            effectiveUserId.ifBlank { "self" },
            effectiveSourceFirmenId.ifBlank { "self" },
            if (serviceBookingMode) "service" else "standard"
        ).joinToString("#")
    }
    var selectedDate by remember(calendarContextKey) { mutableStateOf(kalenderTodayIsoDate()) }
    var selectedBookingCell by remember(calendarContextKey, selectedDate) { mutableStateOf<Pair<String, Int>?>(null) }
    var bookingRequestToken by remember(calendarContextKey) { mutableStateOf(0) }
    var settingsRequestToken by remember(calendarContextKey) { mutableStateOf(0) }
    var kalenderAnsicht by remember(calendarContextKey) { mutableStateOf("Monat") }
    var monthDayOpen by remember(calendarContextKey) { mutableStateOf(false) }
    var profileUser by remember(loginUser?.id, effectiveUserId) { mutableStateOf(loginUser ?: All(id = effectiveUserId)) }
    var kiSchulungen by remember(calendarContextKey) { mutableStateOf<List<All>>(emptyList()) }
    var kiSchulungenRefreshToken by remember(calendarContextKey) { mutableStateOf(0) }
    DisposableEffect(calendarContextKey) {
        val listener: (Event) -> Unit = { kiSchulungenRefreshToken += 1 }
        window.addEventListener("lumatica-schulung-updated", listener)
        onDispose { window.removeEventListener("lumatica-schulung-updated", listener) }
    }
    LaunchedEffect(loginUser?.id, effectiveUserId, effectiveSourceFirmenId) {
        val fallback = loginUser ?: All(id = effectiveUserId)
        val contextPath = if (effectiveSourceFirmenId.isNotBlank()) {
            "kalender/scoped-context/${terminApiPart(effectiveSourceFirmenId)}/${terminApiPart(effectiveUserId)}"
        } else {
            "kalender/context/${terminApiPart(effectiveUserId)}"
        }
        val raw = runCatching { LumaticaApiManager.call(contextPath) }.getOrDefault("")
        profileUser = runCatching { terminScreenJson.decodeFromString(All.AllNames.serializer(), raw) }
            .getOrNull()
            ?: fallback
    }
    LaunchedEffect(allowsKiSchulung, isOwnCalendar, serviceBookingMode, loginUser?.id, kiSchulungenRefreshToken, schulungLiveVersion) {
        kiSchulungen = if (allowsKiSchulung && isOwnCalendar && !serviceBookingMode && loginUser?.zugangDesktop == true) {
            SchulungApi.myTeilnahmen()
                .filter { !it.schulungsModus.equals("PHYSISCH", ignoreCase = true) }
                .sortedWith(compareBy<All> { it.zustand == ZustandManager.Schulung.ABGESCHLOSSEN }.thenBy { it.bezeichnung })
        } else {
            emptyList()
        }
    }

    val displayName = profileDisplayName(profileUser)
    val displayFirma = profileUser.firma
        .ifBlank { loginUser?.firma.orEmpty() }
        .ifBlank { profileUser.kunde }
        .ifBlank { loginUser?.kunde.orEmpty() }
        .ifBlank { "Keine Firma hinterlegt" }
    val displayPosition = profileUser.position
        .ifBlank { "Keine Position hinterlegt" }
    val targetFirmenId = effectiveSourceFirmenId.ifBlank { loginFirmenId }
    val showPrivateCalendarDetails = art.uppercase() == NodeScope.SYS ||
        (loginFirmenId.isNotBlank() && loginFirmenId == targetFirmenId)
    val offeneKiSchulungen = if (allowsKiSchulung && loginUser?.zugangDesktop == true) {
        kiSchulungen.filter { it.zustand != ZustandManager.Schulung.ABGESCHLOSSEN }
    } else emptyList()
    val navigationActions = buildList {
        add(All(key = "book", label = "Termin buchen"))
        offeneKiSchulungen.forEachIndexed { index, schulung ->
            add(All(key = "training:$index", label = "KI-Schulung: ${schulung.bezeichnung.ifBlank { schulung.name }}"))
        }
        if (allowsKiSchulung && loginUser?.zugangDesktop == true && offeneKiSchulungen.isEmpty()) {
            add(All(key = "training-empty", label = "Keine offene KI-Schulung", aktiv = false))
        }
        add(All(key = "calendar-settings", label = "Kalendereinstellungen"))
    }

    Div({
        style {
            width(100.percent)
            height(100.percent)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            justifyContent(JustifyContent.FlexStart)
            alignItems(AlignItems.FlexStart)
            padding(12.px, 10.px, 16.px, 10.px)
            boxSizing("border-box")
            gap(10.px)
        }
    }) {
        KalenderDesktopNavigation(
            name = displayName,
            firma = displayFirma,
            position = displayPosition,
            selectedDate = selectedDate,
            onSelectDate = {
                selectedDate = it
                selectedBookingCell = null
                monthDayOpen = false
            },
            showTaskSwitch = false,
            taskMode = false,
            onTaskModeChange = {},
            view = kalenderAnsicht,
            onViewChange = {
                kalenderAnsicht = it
                selectedBookingCell = null
                monthDayOpen = false
            },
            showSettings = isOwnCalendar && !serviceBookingMode,
            settingsPlaceholder = "Termin buchen",
            settingsOptions = navigationActions,
            onSettingsAction = { action ->
                when (action) {
                    "book" -> {
                        kalenderAnsicht = "Woche"
                        monthDayOpen = false
                        bookingRequestToken += 1
                    }
                    "calendar-settings" -> {
                        kalenderAnsicht = "Woche"
                        monthDayOpen = false
                        settingsRequestToken += 1
                    }
                    else -> if (action.startsWith("training:")) {
                        val schulung = action.removePrefix("training:").toIntOrNull()?.let(offeneKiSchulungen::getOrNull)
                        if (schulung != null) {
                            val baseHash = window.location.hash.substringBefore("?").ifBlank { "#/dashboard" }
                            val id = schulung.schulungId.ifBlank { schulung.id }
                            window.location.hash = "$baseHash?screen=SchulungsFlowScreen&schulungId=$id"
                        }
                    }
                }
            }
        )

        Div({
            style {
                width(100.percent)
                flexGrow(1)
                minHeight(0.px)
                display(DisplayStyle.Grid)
                property(
                    "grid-template-columns",
                    if (kalenderAnsicht == "Monat" && monthDayOpen) {
                        "max-content minmax(0, 1fr)"
                    } else {
                        "minmax(0, 1fr)"
                    }
                )
                gap(if (kalenderAnsicht == "Monat" && monthDayOpen) 0.px else 18.px)
                boxSizing("border-box")
                alignItems(AlignItems.Stretch)
                justifyContent(JustifyContent.Center)
                padding(0.px, 6.px, 0.px, 6.px)
                overflow("hidden")
            }
        }) {
            if (kalenderAnsicht == "Monat") {
                Div({
                    style {
                        property("width", if (monthDayOpen) "auto" else "100%")
                        height(100.percent)
                        minWidth(0.px)
                        minHeight(0.px)
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        justifyContent(JustifyContent.FlexStart)
                        alignItems(if (monthDayOpen) AlignItems.FlexStart else AlignItems.Center)
                        boxSizing("border-box")
                        padding(8.px)
                    }
                }) {
                    Div({
                        style {
                            property("width", "auto")
                            height(100.percent)
                            property("aspect-ratio", "1 / 1")
                            property("max-width", "100%")
                            minHeight(0.px)
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Column)
                            boxSizing("border-box")
                        }
                    }) {
                        Monatsübersicht(
                            selectedDate = selectedDate,
                            art = effectiveArt,
                            kalenderUserId = effectiveUserId,
                            kalenderFirmenId = effectiveSourceFirmenId,
                            showPrivateBookingDetails = showPrivateCalendarDetails,
                            onSelectDate = {
                                selectedDate = it
                                selectedBookingCell = null
                                monthDayOpen = true
                            }
                        )
                    }
                }

                if (monthDayOpen) {
                    Div({
                        style {
                            minWidth(0.px)
                            height(100.percent)
                            minHeight(0.px)
                            overflow("hidden")
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Column)
                            alignItems(AlignItems.Stretch)
                            boxSizing("border-box")
                            padding(8.px, 18.px)
                        }
                    }) {
                        KalenderWeekDetail(
                            selectedDate = selectedDate,
                            art = effectiveArt,
                            kalenderUserId = effectiveUserId,
                            kalenderFirmenId = effectiveSourceFirmenId,
                            ansicht = "Tag",
                            serviceBookingMode = serviceBookingMode,
                            showPrivateBookingDetails = showPrivateCalendarDetails,
                            canManageSlotSettings = isOwnCalendar,
                            fillVisibleHours = true,
                            selectedBookingCell = selectedBookingCell,
                            onSelectedBookingCellChange = { selectedBookingCell = it },
                            bookingRequestToken = bookingRequestToken,
                            settingsRequestToken = settingsRequestToken,
                            bookingTemplate = bookingTemplate,
                            onBookingSaved = onBookingSaved,
                            onNavigateWeek = { delta ->
                                parseKalenderIsoDate(selectedDate)?.let { date ->
                                    selectedDate = kalenderAddDays(date.year, date.month, date.day, delta * 7)
                                    selectedBookingCell = null
                                }
                            }
                        )
                    }
                }
            } else {
                Div({
                    style {
                        width(100.percent)
                        height(100.percent)
                        minWidth(0.px)
                        minHeight(0.px)
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        alignItems(AlignItems.Stretch)
                        boxSizing("border-box")
                        padding(8.px)
                        overflow("hidden")
                    }
                }) {
                    KalenderWeekDetail(
                        selectedDate = selectedDate,
                        art = effectiveArt,
                        kalenderUserId = effectiveUserId,
                        kalenderFirmenId = effectiveSourceFirmenId,
                        ansicht = "Woche",
                        serviceBookingMode = serviceBookingMode,
                        showPrivateBookingDetails = showPrivateCalendarDetails,
                        canManageSlotSettings = isOwnCalendar,
                        fillVisibleHours = true,
                        selectedBookingCell = selectedBookingCell,
                        onSelectedBookingCellChange = { selectedBookingCell = it },
                        bookingRequestToken = bookingRequestToken,
                        settingsRequestToken = settingsRequestToken,
                        bookingTemplate = bookingTemplate,
                        onBookingSaved = onBookingSaved,
                        onNavigateWeek = { delta ->
                            parseKalenderIsoDate(selectedDate)?.let { date ->
                                selectedDate = kalenderAddDays(date.year, date.month, date.day, delta * 7)
                                selectedBookingCell = null
                            }
                        }
                    )
                }
            }
        }
    }
}

private fun profileDisplayName(user: All): String =
    listOf(user.vorname, user.nachname)
        .filter { it.isNotBlank() }
        .joinToString(" ")
        .ifBlank { user.name.ifBlank { user.personalnummer.ifBlank { user.id.ifBlank { "Mein Profil" } } } }
